 
import { authService } from '@/core/sso/oidc/AuthService'
import { RealTimeEventTypes } from '@/enums/RealTimeEventTypes'
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { FailedToNegotiateWithServerError } from '@microsoft/signalr/dist/esm/Errors'
import { injectable } from 'inversify'

export interface IRealtimeEventService {
  connect(): Promise<void>
  disconnect(): Promise<void>
  subscribe<T>(eventType: RealTimeEventTypes, callback: (event: T) => Promise<void>): RealTimeEventSubscription
}

export interface RealTimeEventSubscription {
  unsubscribe(): boolean
}

export interface EventHandler<T> {
  eventType: RealTimeEventTypes
  handle: (event: T) => Promise<void>
}

const CONNECTION_MAX_RETRIES = 3
const CONNECTION_RETRY_DELAY = 5000

@injectable()
export class RealTimeEventService implements IRealtimeEventService {
  private isConnected: boolean = false
  private connectionRetries = 0
  protected connection: HubConnection
  protected readonly eventHandlers: EventHandler<any>[] = []
  protected hub: string = ''

  constructor(hub: string) {
    this.hub = hub

    this.connection = new HubConnectionBuilder()
      .withUrl(`${import.meta.env.VITE_API_URL}/real-time-events/${this.hub}`, {
        headers: { 'X-UserId': 'SiteManagement' }
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Error)
      .build()
  }

  buildConnection() {
    this.connection = new HubConnectionBuilder()
      .withUrl(`${import.meta.env.VITE_API_URL}/real-time-events/${this.hub}`, {
        headers: { 'X-UserId': 'SiteManagement', Authorization: `Bearer ${authService.user.value?.access_token}`, 'API-KEY': `${import.meta.env.VITE_API_KEY}` }
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Error)
      .build()
  }

  async connect(): Promise<void> {
    if (this.isConnected) {
      return
    }

    if (!(await authService.checkAuthentication())) {
      await authService.login()
    }

    this.buildConnection()

    try {
      await this.connection.start()
      this.isConnected = true
    } catch (error: unknown) {
      if (error instanceof FailedToNegotiateWithServerError) {
        if (!this.isConnected && this.connectionRetries < CONNECTION_MAX_RETRIES) {
          this.connectionRetries += 1
          setTimeout(() => this.connect(), CONNECTION_RETRY_DELAY)
        }
      } else {
        throw error
      }
    }
  }

  async disconnect(): Promise<void> {
    this.connectionRetries = 0

    if (this.isConnected) {
      await this.connection.stop()
      this.isConnected = false
    }
  }

  subscribe<T>(eventType: RealTimeEventTypes, eventHandler: (event: T) => Promise<void>): RealTimeEventSubscription {
    const newEventHandler = {
      eventType,
      handle: eventHandler
    }

    this.eventHandlers.push(newEventHandler)

    const eventHandlers = this.eventHandlers
    return {
      unsubscribe: (): boolean => {
        try {
          const index = eventHandlers.indexOf(newEventHandler)
          if (index > -1) {
            eventHandlers.splice(index, 1)
          }
          return true
        } catch {
          return false
        }
      }
    }
  }
}
